import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  isSameYear,
} from "date-fns";

const DAYS_BEFORE_ABSOLUTE_DATE = 7;

/**
 * Formats a timestamp as a compact relative age, ageing to an absolute date past a week.
 * @param date - The timestamp to format
 * @param now - Reference point, defaults to the current time
 * @returns A compact age such as "just now", "2h ago", "3d ago", or "Jun 12"
 */
export function formatCompactAge(date: Date, now: Date = new Date()): string {
  const minutes = differenceInMinutes(now, date);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = differenceInHours(now, date);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = differenceInDays(now, date);
  if (days < DAYS_BEFORE_ABSOLUTE_DATE) {
    return `${days}d ago`;
  }

  return isSameYear(date, now)
    ? format(date, "MMM d")
    : format(date, "MMM d, yyyy");
}

/**
 * Formats a timestamp as an absolute date and time for tooltip display.
 * @param date - The timestamp to format
 * @returns An absolute date and time string
 */
export function formatAbsoluteTimestamp(date: Date): string {
  return format(date, "MMM d, yyyy 'at' h:mm a");
}
